// File: src/pages/SettingsPage.js
// This is the complete file with all sections restored and the loading-crash bug fixed.
import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function SettingsPage({ siteSettings, setSiteSettings }) {

  // CRITICAL FIX: If siteSettings or its nested properties are not yet loaded, 
  // show a loading message instead of trying to render the form and crashing.
  if (!siteSettings || !siteSettings.socialLinks || !siteSettings.integrations || !siteSettings.monetization) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-8">
        <h1 className="text-3xl font-bold text-lime">Loading Settings...</h1>
      </div>
    );
  }

  const handleOfflineToggle = () => {
    setSiteSettings(prev => ({ ...prev, isOffline: !prev.isOffline }));
  };

  const handleSocialChange = (e) => {
    const { id, value } = e.target;
    setSiteSettings(prev => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [id]: { ...prev.socialLinks?.[id], url: value } }
    }));
  };

  const handleSocialToggle = (platform) => {
    setSiteSettings(prev => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [platform]: { ...prev.socialLinks?.[platform], isActive: !prev.socialLinks?.[platform]?.isActive } }
    }));
  };

  const handleIntegrationChange = (e) => {
    const { id, value } = e.target;
    setSiteSettings(prev => ({
        ...prev,
        integrations: { ...prev.integrations, [id]: value }
    }));
  };
  
  const handleMonetizationChange = (e) => {
    const { id, value } = e.target;
    setSiteSettings(prev => ({
        ...prev,
        monetization: { ...prev.monetization, [id]: value }
    }));
  };

  const handleSaveSettings = async () => {
    try {
      await axios.post('/api/settings', siteSettings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please check the console for details.');
    }
  };

  // Use ?. (optional chaining) to safely access nested properties.
  // This prevents crashes if a property like 'integrations' doesn't exist yet.
  const integrations = siteSettings?.integrations || {};
  const monetization = siteSettings?.monetization || {};
  const socialLinks = siteSettings?.socialLinks || {};

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-black shadow-md">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <img src="/Logo.png" alt="R.A.W. Fitness Logo" className="h-12 w-auto" />
            <h1 className="text-xl font-bold text-lime">Site Settings</h1>
          </div>
          <nav>
            <Link to="/admin" className="text-white hover:text-lime transition duration-300">Back to Dashboard</Link>
          </nav>
        </div>
      </header>
      <main className="container mx-auto px-6 py-8">
        
        <div className="bg-gray-800 p-8 rounded-lg shadow-lg mb-8">
          <h2 className="text-3xl font-bold mb-6 text-magenta">Site Status</h2>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Website Offline / Under Construction</h3>
              <p className="text-gray-400">When activated, visitors will see a maintenance page instead of the live site.</p>
            </div>
            <label htmlFor="offlineToggle" className="flex items-center cursor-pointer">
              <div className="relative">
                <input type="checkbox" id="offlineToggle" className="sr-only" checked={siteSettings.isOffline} onChange={handleOfflineToggle} />
                <div className="block bg-gray-600 w-14 h-8 rounded-full"></div>
                <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition ${siteSettings.isOffline ? 'transform translate-x-6 bg-lime' : ''}`}></div>
              </div>
              <div className="ml-3 font-medium">{siteSettings.isOffline ? 'Active' : 'Inactive'}</div>
            </label>
          </div>
        </div>

        <div className="bg-gray-800 p-8 rounded-lg shadow-lg mb-8">
          <h2 className="text-3xl font-bold mb-6 text-lime">Social Media Links</h2>
          <form><div className="space-y-6">
              {Object.keys(socialLinks).map(platform => (
                <div key={platform}>
                  <label className="block text-gray-400 text-sm font-bold mb-2 capitalize" htmlFor={platform}>{platform === 'x' ? 'X (Twitter)' : platform} URL</label>
                  <div className="flex items-center space-x-4">
                    <input id={platform} type="text" value={socialLinks[platform]?.url || ''} onChange={handleSocialChange} className="appearance-none block w-full bg-gray-700 text-white border border-gray-600 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-gray-600 focus:border-lime" />
                    <label htmlFor={`${platform}-toggle`} className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input type="checkbox" id={`${platform}-toggle`} className="sr-only" checked={socialLinks[platform]?.isActive} onChange={() => handleSocialToggle(platform)} />
                        <div className="block bg-gray-600 w-14 h-8 rounded-full"></div>
                        <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition ${socialLinks[platform]?.isActive ? 'transform translate-x-6 bg-lime' : ''}`}></div>
                      </div>
                    </label>
                  </div>
                </div>
              ))}
            </div></form>
        </div>

        <div className="bg-gray-800 p-8 rounded-lg shadow-lg mb-8">
            <h2 className="text-3xl font-bold mb-6 text-magenta">Third-Party Integrations</h2>
            <form><div className="space-y-6">
                    <div>
                        <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="googleAnalyticsId">Google Analytics ID</label>
                        <input id="googleAnalyticsId" type="text" value={integrations.googleAnalyticsId || ''} onChange={handleIntegrationChange} placeholder="G-XXXXXXXXXX" className="appearance-none block w-full bg-gray-700 text-white border border-gray-600 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-gray-600 focus:border-magenta" />
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="googleTagManagerId">Google Tag Manager ID</label>
                        <input id="googleTagManagerId" type="text" value={integrations.googleTagManagerId || ''} onChange={handleIntegrationChange} placeholder="GTM-XXXXXXX" className="appearance-none block w-full bg-gray-700 text-white border border-gray-600 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-gray-600 focus:border-magenta" />
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="googleSearchConsoleId">Google Search Console Verification ID</label>
                        <input id="googleSearchConsoleId" type="text" value={integrations.googleSearchConsoleId || ''} onChange={handleIntegrationChange} placeholder="Your verification meta tag content" className="appearance-none block w-full bg-gray-700 text-white border border-gray-600 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-gray-600 focus:border-magenta" />
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="youtubeApiKey">YouTube Data API Key</label>
                        <input id="youtubeApiKey" type="text" value={integrations.youtubeApiKey || ''} onChange={handleIntegrationChange} placeholder="Your YouTube API Key" className="appearance-none block w-full bg-gray-700 text-white border border-gray-600 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-gray-600 focus:border-magenta" />
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="adSenseId">Google AdSense Publisher ID</label>
                        <input id="adSenseId" type="text" value={integrations.adSenseId || ''} onChange={handleIntegrationChange} placeholder="pub-0000000000000000" className="appearance-none block w-full bg-gray-700 text-white border border-gray-600 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-gray-600 focus:border-magenta" />
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="reCaptchaKey">Google reCaptcha Site Key</label>
                        <input id="reCaptchaKey" type="text" value={integrations.reCaptchaKey || ''} onChange={handleIntegrationChange} placeholder="Your reCaptcha v3 Site Key" className="appearance-none block w-full bg-gray-700 text-white border border-gray-600 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-gray-600 focus:border-magenta" />
                    </div>
                </div></form>
        </div>

        <div className="bg-gray-800 p-8 rounded-lg shadow-lg">
            <h2 className="text-3xl font-bold mb-6 text-lime">Monetization</h2>
            <form><div className="space-y-6">
                    <div>
                        <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="affiliateLink">Default Affiliate Link</label>
                        <input id="affiliateLink" type="text" value={monetization.affiliateLink || ''} onChange={handleMonetizationChange} placeholder="https://youraffiliatelink.com" className="appearance-none block w-full bg-gray-700 text-white border border-gray-600 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-gray-600 focus:border-lime" />
                        <p className="text-xs text-gray-500 mt-1">This can be a general link to an affiliate partner.</p>
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm font-bold mb-2">E-commerce & Subscriptions</label>
                        <p className="text-gray-400">Integrations for selling digital products or managing premium subscriptions will appear here once configured.</p>
                    </div>
                </div></form>
        </div>

        <div className="mt-8 text-right">
          <button onClick={handleSaveSettings} className="bg-magenta text-black font-bold py-3 px-6 rounded-lg hover:bg-opacity-80 transition duration-300 glow-magenta" type="button">
            Save All Settings
          </button>
        </div>
      </main>
    </div>
  );
}

export default SettingsPage;


